import { useCallback, useEffect, useRef, useState } from 'react'
import { listDemoWebReleases, type DemoWebReleaseList } from '../../../services/ownerDemoLaneService'

export type DemoWebReleasesState = {
  data: DemoWebReleaseList | null
  loading: boolean
  error: string | null
  reload: () => Promise<void>
}

type Outcome = { ok: true; value: DemoWebReleaseList } | { ok: false; message: string }

const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error'

const fetchReleases = (): Promise<Outcome> =>
  listDemoWebReleases().then(
    (value): Outcome => ({ ok: true, value }),
    (error: unknown): Outcome => ({ ok: false, message: messageOf(error) }),
  )

/** Loads the demo website releases once (when enabled) and again on `reload()`. */
export function useDemoWebReleases(enabled: boolean): DemoWebReleasesState {
  const [data, setData] = useState<DemoWebReleaseList | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)
  const mounted = useRef(true)

  const apply = useCallback((outcome: Outcome) => {
    if (!mounted.current) return
    if (outcome.ok) {
      setData(outcome.value)
      setError(null)
    } else {
      setError(outcome.message)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    mounted.current = true
    if (enabled) void fetchReleases().then(apply)
    return () => { mounted.current = false }
  }, [enabled, apply])

  const reload = useCallback(async () => {
    setLoading(true)
    apply(await fetchReleases())
  }, [apply])

  return { data, loading, error, reload }
}
